import React, { useState } from 'react';
import { initEditingViewState, ViewsContext } from './ViewsContext';
import { EditingViewState } from '../../components/settings/viewsManagement/EditingViewState';
import { useNotificationContext } from '../../global/hooks/useNotificationContext';
import { useIsMutating } from '@tanstack/react-query';
import { mutateViewsKeys } from './mutateKeyFactory';
import { useCurrentUserContext } from '../currentUser/CurrentUserContext';
import { useUpdateView } from './useUpdateView';
import { UpdateViewRequest } from '../../Model/requests/UpdateViewRequest';
import { useGetViews } from './useGetViews';
import { USER_VIEW_SETTINGS_KEY } from '../../config/consts';
import { useGlobalizationContext } from '../globalization/GlobalizationContext';
import { useTranslation } from '../globalization/useTranslation';
import { useGetView } from './useGetView';
import { StorageHelper } from '../../helpers/StorageHelper';

interface ViewsProviderProps {
  children?: React.ReactNode;
}

export const ViewsProvider: React.FC<ViewsProviderProps> = ({ children }) => {
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { selectedIndex } = useCurrentUserContext();
  const { data: views } = useGetViews(selectedIndex?.id);
  const [selectedView, setSelectedView] = useState<number | undefined>(undefined);
  const { data: currentViewDetails } = useGetView(selectedView, selectedLocale);
  const [editingViewStateOverrides, setEditingViewStateOverrides] = useState<EditingViewState | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [overridesViewId, setOverridesViewId] = useState<number | undefined>(undefined);

  const editingViewState = React.useMemo(() => {
    if (overridesViewId !== selectedView) {
      if (!currentViewDetails) {
        return initEditingViewState;
      }

      const fields = currentViewDetails.fields.map(field => ({
        id: field.id,
        name: field.displayName || field.name,
        width: field.width,
        facetable: field.facetable,
        facetsCount: field.facetsCount,
        isDocumentLink: field.isDocumentLink
      }));

      return {
        fields,
        globalFilters: currentViewDetails.globalFilters
      };
    }

    if (editingViewStateOverrides) {
      return editingViewStateOverrides;
    }

    if (!currentViewDetails) {
      return initEditingViewState;
    }

    const fields = currentViewDetails.fields.map(field => ({
      id: field.id,
      name: field.displayName || field.name,
      width: field.width,
      facetable: field.facetable,
      facetsCount: field.facetsCount,
      isDocumentLink: field.isDocumentLink
    }));

    return {
      fields,
      globalFilters: currentViewDetails.globalFilters
    };
  }, [editingViewStateOverrides, currentViewDetails, selectedView, overridesViewId]);

  const setEditingViewState = (newState: EditingViewState) => {
    setEditingViewStateOverrides(newState);
    setOverridesViewId(selectedView);
    setHasUnsavedChanges(true);
  };

  const getStoredViewSelections = (): Record<number, number> => {
    const stored = StorageHelper.get(USER_VIEW_SETTINGS_KEY);
    return stored ? JSON.parse(stored) : {};
  };

  const storeViewSelection = (indexId: number, viewId: number) => {
    const stored = getStoredViewSelections();
    stored[indexId] = viewId;
    StorageHelper.set(USER_VIEW_SETTINGS_KEY, JSON.stringify(stored));
  };

  React.useEffect(() => {
    setSelectedView(undefined);
  }, [selectedIndex?.id]);

  React.useEffect(() => {
    if (!views?.length || selectedIndex?.id === undefined) {
      return;
    }

    if (selectedView !== undefined) {
      return;
    }

    const storedViewSelections = getStoredViewSelections();
    const storedViewId = storedViewSelections[selectedIndex.id];

    if (storedViewId != null) {
      const foundView = views.find(view => view.id === storedViewId);
      if (foundView) {
        setSelectedView(storedViewId);
        return;
      }
    }

    setSelectedView(views[0].id);
  }, [views, selectedView, selectedIndex?.id]);

  const { mutate: mutateUpdatePrivateView } = useUpdateView();
  const { showToast } = useNotificationContext();

  const handleUpdateView = (updateViewRequest: UpdateViewRequest) => {
    mutateUpdatePrivateView(updateViewRequest, {
      onSuccess: data => {
        if (data) {
          showToast({
            type: 'success',
            message: translate('custom.notifications.views.update')
          });
        }
      }
    });
  };

  const handleSetSelectedView = (viewId: number | undefined) => {
    if (viewId !== undefined && selectedIndex?.id !== undefined) {
      storeViewSelection(selectedIndex.id, viewId);
    }
    setSelectedView(viewId);
  };

  const selectedViewData = React.useMemo(() => {
    if (!selectedView || !views) {
      return undefined;
    }
    return views.find(view => view.id === selectedView);
  }, [selectedView, views]);

  const isRemoveViewMutation = useIsMutating({ mutationKey: mutateViewsKeys.deleteView }) > 0;

  return (
    <ViewsContext.Provider
      value={{
        views,
        selectedView,
        setSelectedView: handleSetSelectedView,
        selectedViewData,
        mutateViewLoading: isRemoveViewMutation,
        onUpdateView: handleUpdateView,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        editingViewState,
        setEditingViewState
      }}
    >
      {children}
    </ViewsContext.Provider>
  );
};
